import { create } from 'zustand';
import { ReconciliationSettings, DataSet } from '../types';
import { loadLocal, saveLocal } from '../services/storage';

const DEFAULT_SETTINGS: ReconciliationSettings = {
    taxTolerance: 10.0,
    taxTolerancePercent: 1.0,
    dateTolerance: 2,
    fuzzyMatch: true,
    ignoreSpecialCharsInInv: true,
    matchB2BOnly: false,
    multiLineMatch: true,
    autoHighlight: true,
    duplicateDetection: true,
    emptyCellHighlight: true,
    caseSensitive: false,
    enableUndoRedo: true,
    enableGstinMatch: true,
    enableFuzzyGstinMatch: true,
    enableInvoiceNumberMatch: true,
    enableFuzzyInvoiceNumberMatch: true,
    enableTaxValueMatch: true,
    enableTaxValueToleranceMatch: true,
    enableInvoiceDateMatch: true,
    enableInvoiceDateToleranceMatch: true,
    gstinConfig: {
        enableFuzzy: true,
        similarityThreshold: 85,
        replacements: [
            { from: 'O', to: '0' },
            { from: 'I', to: '1' },
            { from: 'B', to: '8' }
        ]
    },
    invoiceConfig: {
        enableFuzzy: true,
        similarityThreshold: 80
    },
    partialMatchRules: [],
    additionalReconSettings: {
        enabled: true,
        gstin: { exact: true, fuzzy: false },
        invoiceNumber: { exact: true, fuzzy: false },
        date: { exact: true, tolerance: false },
        taxValue: { exact: true, tolerance: false },
    },
    panMatch: true,
    users: [
        { username: 'admin', password: 'admin', role: 'admin', permissions: ['all'] },
        { username: 'manager', password: 'password', role: 'user', permissions: ['read', 'write'] }
    ],
    storagePath: '',
    isEncrypted: false,
    masterPasswordSet: false,
    masterPasswordHash: ''
};

interface SettingsState {
    settings: ReconciliationSettings;
    encryptionKey: string | null;
    setSettings: (settings: ReconciliationSettings) => void;
    setEncryptionKey: (key: string | null) => void;
    loadSettings: () => Promise<void>;
    saveSettings: (settings: ReconciliationSettings) => void;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
    settings: DEFAULT_SETTINGS,
    encryptionKey: null,
    setSettings: (settings) => set({ settings }),
    setEncryptionKey: (key) => set({ encryptionKey: key }),
    loadSettings: async () => {
        try {
            // Settings are usually NOT encrypted, or they use a default key if basic hidden?
            // Actually, settings houses the "masterPasswordSet" flag.
            // Let's keep settings unencrypted for now to detect if encryption is needed.
            const storedSettings = await loadLocal('app_settings', DEFAULT_SETTINGS);
            if (storedSettings && (storedSettings as any).settings) {
                const loaded = (storedSettings as any).settings;
                // Ensure users array exists. Only reset if it's completely missing or empty.
                if (!loaded.users || !Array.isArray(loaded.users) || loaded.users.length === 0) {
                    loaded.users = DEFAULT_SETTINGS.users;
                } else {
                    // Safety: Ensure admin is always reachable if something went wrong during deletion
                    const hasAdmin = (loaded.users as any[]).some(u => u.role === 'admin');
                    if (!hasAdmin) {
                        loaded.users.push(DEFAULT_SETTINGS.users[0]);
                    }
                }
                set({ settings: loaded });
            } else {
                // If no settings found at all, use defaults
                set({ settings: DEFAULT_SETTINGS });
            }
        } catch (err) {
            console.error("Failed to load settings:", err);
        }
    },
    saveSettings: (newSettings) => {
        set({ settings: newSettings });
        saveLocal('app_settings', { settings: newSettings } as DataSet, newSettings);
    }
}));
